import SectionHeader from "@/components/site/SectionHeader";
import PackageCard from "@/components/site/PackageCard";
import { PACKAGE_GROUPS, HOSTING_PLANS } from "@/lib/packages";
import { Link } from "react-router-dom";
import { Gift, ArrowRight, CheckCircle2, Sparkles, DollarSign, Check, X } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { offerCatalogSchema, faqSchema, breadcrumbSchema } from "@/components/seo/schema";
import { FREE_PROGRAM, COMPARISON, FREE_FAQS } from "@/lib/freeProgram";

function yn(value) {
  const v = String(value).toLowerCase();
  if (v === "yes" || v === "included") return <Check className="w-4 h-4 text-blue-400 inline" />;
  if (v === "none" || v === "no") return <X className="w-4 h-4 text-gray-500 inline" />;
  return value;
}

export default function Packages() {
  return (
    <div data-testid="page-packages">
      <Seo
        title="Pricing & Packages — Free Program vs Traditional | Equinox Systems"
        description="Compare the Free Custom Website Program ($0 build, $200/mo, 6-month minimum) with traditional website purchase. Plus web, app, CRM, CMS, API, AI packages and hosting from $29/mo."
        path="/packages"
        schemas={[
          offerCatalogSchema(),
          faqSchema(FREE_FAQS),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Packages", path: "/packages" }]),
        ]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeader
            as="h1"
            eyebrow="Pricing & Packages"
            title="Two ways to get a premium custom website"
            description="Choose the Free Custom Website Program with no upfront build cost, or purchase your website outright with immediate ownership. Every plan is fully custom-built, mobile optimized, and SEO-ready."
          />

          {/* SIDE-BY-SIDE COMPARISON */}
          <div className="grid lg:grid-cols-2 gap-6" data-testid="pricing-comparison">
            {[COMPARISON.free, COMPARISON.traditional].map((opt, idx) => {
              const isFree = idx === 0;
              return (
                <div
                  key={opt.name}
                  className={`rounded-3xl p-8 md:p-10 border ${
                    isFree
                      ? "border-blue-500/40 bg-gradient-to-br from-blue-500/10 to-transparent"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  data-testid={`pricing-option-${isFree ? "free" : "traditional"}`}
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-400">
                    {isFree ? <Sparkles className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                    {isFree ? "Option A" : "Option B"}
                  </div>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold">{opt.name}</h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-display text-5xl font-bold eq-gradient-text">{opt.price}</span>
                    <span className="text-sm text-gray-400">{opt.priceNote}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-400">{opt.monthly} · {opt.commitment}</div>

                  <dl className="mt-6 divide-y divide-white/10 text-sm">
                    {opt.rows.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between py-2.5">
                        <dt className="text-gray-400">{k}</dt>
                        <dd className="text-white font-medium text-right">{yn(v)}</dd>
                      </div>
                    ))}
                  </dl>

                  <Link
                    to={isFree ? "/apply" : "/quote"}
                    className={`${isFree ? "btn-primary" : "btn-ghost"} w-full mt-7`}
                    data-testid={`pricing-cta-${isFree ? "free" : "traditional"}`}
                  >
                    {isFree ? "Apply For A Free Website" : "Request a Custom Quote"}{" "}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>

          {/* FREE PROGRAM FEATURE STRIP */}
          <div className="mt-10 glass rounded-3xl p-8 md:p-10" data-testid="free-program-strip">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-400">Free Custom Website Program</div>
                <h3 className="font-display text-2xl font-semibold mt-1">
                  {FREE_PROGRAM.build} build · {FREE_PROGRAM.monthly}{FREE_PROGRAM.monthlySuffix} · {FREE_PROGRAM.commitment}
                </h3>
              </div>
              <Link to="/apply" className="btn-primary shrink-0" data-testid="free-strip-apply-btn">
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {FREE_PROGRAM.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-gray-200">
                  <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          {/* TRADITIONAL PACKAGE GROUPS */}
          <div className="mt-20">
            <SectionHeader
              eyebrow="Traditional Packages"
              title="Buy your website outright — own it immediately"
              description="Prefer immediate ownership? Choose a one-time build below. Every traditional project begins with a 10% non-refundable deposit; the remaining balance is scheduled through milestones or final invoice."
            />
          </div>

          {PACKAGE_GROUPS.map((group) => (
            <div key={group.id} className="mt-16" data-testid={`pkg-group-${group.id}`}>
              <div className="mb-8 flex items-end justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-blue-400">{group.id.toUpperCase()}</div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1">{group.title}</h3>
                  <p className="text-gray-400 mt-1 text-sm max-w-2xl">{group.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.packages.map((p) => <PackageCard key={p.id} pkg={p} />)}
              </div>
            </div>
          ))}

          {/* HOSTING */}
          <div className="mt-20" data-testid="pkg-group-hosting">
            <div className="mb-8">
              <div className="text-xs uppercase tracking-widest text-blue-400">HOSTING</div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1">Hosting &amp; Maintenance</h3>
              <p className="text-gray-400 mt-1 text-sm max-w-2xl">
                Secure, fast hosting with daily backups, SSL, and monitoring. Hosting clients receive discounted maintenance rates based on their hosting plan, with Premium Hosting including maintenance at no additional cost.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {HOSTING_PLANS.map((p) => <PackageCard key={p.id} pkg={p} />)}
            </div>
          </div>

          {/* Discount banner */}
          <div className="mt-20 rounded-3xl p-10 md:p-12 border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-transparent backdrop-blur-xl flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/15 border border-blue-500/40 grid place-items-center text-blue-300">
                <Gift className="w-6 h-6" strokeWidth={1.7} />
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl font-semibold">15% Discount Available</div>
                <p className="text-sm text-gray-400 mt-2 max-w-xl">
                  Military, veterans, first responders, teachers, and healthcare workers. Verification may be required.
                </p>
              </div>
            </div>
            <Link to="/quote" className="btn-outline-blue" data-testid="packages-discount-btn">
              Claim in Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* SEO / crawlable FAQ content */}
          <div className="mt-20 max-w-3xl">
            <h2 className="font-display text-2xl font-semibold mb-6">Free Custom Website Program — FAQs</h2>
            <div className="space-y-5">
              {FREE_FAQS.map((f) => (
                <div key={f.q} className="glass rounded-2xl p-5">
                  <h3 className="font-display text-base text-white">{f.q}</h3>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/apply" className="btn-primary" data-testid="packages-apply-btn">
                Apply For A Free Website <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/faq" className="btn-ghost" data-testid="packages-faq-link">View All FAQs</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
